import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  FileText, Search, Filter, Calendar, User, Building2,
  RefreshCw, ChevronLeft, ChevronRight, Activity, Shield,
  Plus, Edit, Trash2, Power, Settings, Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import platformApi from '@/lib/platformApi';

const ACTION_ICONS = {
  hospital_created: Plus,
  hospital_updated: Edit,
  hospital_suspended: Power,
  hospital_active: Power,
  features_updated: Settings,
  default: Activity
};

const ACTION_COLORS = {
  hospital_created: 'text-emerald-400 bg-emerald-500/20',
  hospital_updated: 'text-blue-400 bg-blue-500/20',
  hospital_suspended: 'text-red-400 bg-red-500/20',
  hospital_active: 'text-emerald-400 bg-emerald-500/20',
  features_updated: 'text-purple-400 bg-purple-500/20',
  default: 'text-slate-400 bg-slate-500/20'
};

export default function PlatformAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');

  useEffect(() => {
    fetchLogs();
  }, [page, filterAction, filterEntity]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      if (filterAction) params.append('action', filterAction);
      if (filterEntity) params.append('entity_type', filterEntity);

      const response = await platformApi.get(`/platform/audit-logs?${params}`);
      
      setLogs(response.data.logs);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getActionIcon = (action) => {
    return ACTION_ICONS[action] || ACTION_ICONS.default;
  };

  const getActionColor = (action) => {
    return ACTION_COLORS[action] || ACTION_COLORS.default;
  };

  const formatAction = (action) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Platform Audit Logs</h1>
          <p className="text-slate-400">Security events and administrative actions</p>
        </div>
        <Button 
          onClick={() => { setPage(1); fetchLogs(); }}
          variant="outline"
          className="border-slate-600 text-slate-300 hover:bg-slate-700"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={filterAction}
              onChange={(e) => { setFilterAction(e.target.value); setPage(1); }}
              className="px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="filter-action-select"
            >
              <option value="">All Actions</option>
              <option value="hospital_created">Hospital Created</option>
              <option value="hospital_updated">Hospital Updated</option>
              <option value="hospital_suspended">Hospital Suspended</option>
              <option value="hospital_active">Hospital Activated</option>
              <option value="features_updated">Features Updated</option>
            </select>
            <select
              value={filterEntity}
              onChange={(e) => { setFilterEntity(e.target.value); setPage(1); }}
              className="px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="filter-entity-select"
            >
              <option value="">All Entities</option>
              <option value="hospital">Hospital</option>
              <option value="feature_flag">Feature Flag</option>
              <option value="subscription">Subscription</option>
            </select>
            {(filterAction || filterEntity) && (
              <Button 
                variant="ghost"
                onClick={() => { setFilterAction(''); setFilterEntity(''); setPage(1); }}
                className="text-slate-400 hover:text-white"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <p className="text-sm text-slate-400">
        Showing {logs.length} of {total} audit logs
      </p>

      {/* Logs List */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
            </div>
          ) : logs.length > 0 ? (
            <div className="divide-y divide-slate-700">
              {logs.map((log) => {
                const { date, time } = formatTimestamp(log.timestamp);
                const ActionIcon = getActionIcon(log.action);
                const actionColor = getActionColor(log.action);
                
                return (
                  <div key={log.id} className="p-4 hover:bg-slate-700/30 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${actionColor}`}>
                        <ActionIcon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-medium">
                            {formatAction(log.action)}
                          </span>
                          {log.entity_name && (
                            <span className="text-slate-400">
                              - {log.entity_name}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {log.actor_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {log.entity_type}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {time}
                          </span>
                          {log.ip_address && (
                            <span className="text-slate-500">
                              IP: {log.ip_address}
                            </span>
                          )}
                        </div>
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="mt-2 p-2 bg-slate-700/30 rounded text-xs text-slate-400 font-mono overflow-x-auto">
                            {JSON.stringify(log.details, null, 2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No audit logs found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <span className="text-slate-400 text-sm">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-50"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
